import { useState, useCallback } from 'react';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  UploadCloud,
  Link,
  Unlink,
  Search,
  ExternalLink,
  Users,
  Building2,
  Briefcase,
  Mail,
  Phone,
} from 'lucide-react';
import {
  useHubspotImports,
  useUploadHubspotCsv,
  useHubspotImportPreview,
  useSubmitHubspotMapping,
  useHubspotImport,
  useHubspotOAuthStatus,
  useDisconnectHubspot,
  useHubspotApiDeals,
  useHubspotApiPipelines,
  useImportHubspotApiDeals,
  useHubspotApiContacts,
  useImportHubspotApiContacts,
  useHubspotApiCompanies,
  useImportHubspotApiCompanies,
} from '@/hooks/useHubspotImport';
import { usePipelines } from '@/hooks/usePipelines';
import { hubspotImportApi } from '@/lib/api';
import { Button, Card, CardHeader, CardBody, Modal, Select, Skeleton } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import { formatRelativeTime } from '@/lib/utils';
import type { HubspotImport } from '@/types';

const STATUS_BADGE: Record<
  HubspotImport['status'],
  { variant: 'warning' | 'info' | 'success' | 'error'; label: string }
> = {
  MAPPING: { variant: 'warning', label: 'Mapping' },
  PROCESSING: { variant: 'info', label: 'Processing' },
  COMPLETED: { variant: 'success', label: 'Completed' },
  FAILED: { variant: 'error', label: 'Failed' },
};

const TARGET_FIELDS = [
  { value: 'skip', label: '-- Skip this column --' },
  { value: 'contact.firstName', label: 'Contact: First Name' },
  { value: 'contact.lastName', label: 'Contact: Last Name' },
  { value: 'contact.fullName', label: 'Contact: Full Name' },
  { value: 'contact.email', label: 'Contact: Email' },
  { value: 'contact.phone', label: 'Contact: Phone' },
  { value: 'contact.jobTitle', label: 'Contact: Job Title' },
  { value: 'contact.linkedinUrl', label: 'Contact: LinkedIn URL' },
  { value: 'organization.name', label: 'Organization: Name' },
  { value: 'organization.domain', label: 'Organization: Domain' },
  { value: 'organization.website', label: 'Organization: Website' },
  { value: 'organization.industry', label: 'Organization: Industry' },
  { value: 'organization.city', label: 'Organization: City' },
  { value: 'organization.state', label: 'Organization: State' },
  { value: 'organization.country', label: 'Organization: Country' },
  { value: 'organization.size', label: 'Organization: Size' },
  { value: 'deal.title', label: 'Deal: Title' },
  { value: 'deal.value', label: 'Deal: Value' },
  { value: 'deal.stage', label: 'Deal: Stage' },
  { value: 'deal.pipeline', label: 'Deal: Pipeline' },
  { value: 'deal.owner', label: 'Deal: Owner' },
  { value: 'deal.dueDate', label: 'Deal: Due Date' },
  { value: 'deal.description', label: 'Deal: Description' },
];

type ImportTab = 'hubspot' | 'csv';

export default function HubspotImportSettings() {
  const { data: imports, isLoading } = useHubspotImports();
  const { data: oauthStatus, isLoading: loadingOAuth } = useHubspotOAuthStatus();
  const disconnectMutation = useDisconnectHubspot();
  const [showImport, setShowImport] = useState(false);
  const [activeTab, setActiveTab] = useState<ImportTab>('hubspot');

  const handleConnect = async () => {
    const { url } = await hubspotImportApi.getOAuthUrl();
    window.location.href = url;
  };

  if (isLoading || loadingOAuth) {
    return <Skeleton className="h-64 w-full rounded-xl" />;
  }

  const isConnected = oauthStatus && oauthStatus.isActive;

  return (
    <div className="space-y-6">
      {/* OAuth Connection Panel */}
      <Card>
        <CardHeader
          title="HubSpot Connection"
          subtitle="Connect your HubSpot account to import deals directly via API, or use CSV upload."
        />
        <CardBody>
          {isConnected ? (
            <div className="flex items-center justify-between rounded-lg bg-success/5 border border-success/20 p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center">
                  <Link className="w-4 h-4 text-success" />
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary">Connected to HubSpot</p>
                  <p className="text-xs text-grey-500">
                    Portal: {oauthStatus.portalId || 'N/A'}
                    {oauthStatus.metadata?.lastSyncAt && (
                      <> &middot; Last sync: {formatRelativeTime(oauthStatus.metadata.lastSyncAt)}</>
                    )}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => disconnectMutation.mutate()}
                loading={disconnectMutation.isPending}
              >
                <Unlink className="w-3.5 h-3.5" />
                Disconnect
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between rounded-lg bg-grey-50 border border-grey-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-grey-200 flex items-center justify-center">
                  <ExternalLink className="w-4 h-4 text-grey-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary">Not connected</p>
                  <p className="text-xs text-grey-500">Connect to browse and import deals directly from HubSpot</p>
                </div>
              </div>
              <Button size="sm" onClick={handleConnect}>
                <Link className="w-3.5 h-3.5" />
                Connect HubSpot
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Import Tabs */}
      <Card padding="md">
        <div className="flex items-center gap-1 border-b border-grey-200 mb-4">
          <button
            onClick={() => setActiveTab('hubspot')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'hubspot'
                ? 'border-primary text-primary'
                : 'border-transparent text-grey-500 hover:text-secondary'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <Search className="w-3.5 h-3.5" />
              Import from HubSpot
            </span>
          </button>
          <button
            onClick={() => setActiveTab('csv')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'csv'
                ? 'border-primary text-primary'
                : 'border-transparent text-grey-500 hover:text-secondary'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <Upload className="w-3.5 h-3.5" />
              Import from CSV
            </span>
          </button>
        </div>

        {activeTab === 'hubspot' && (
          <HubspotApiImportTab isConnected={!!isConnected} onConnect={handleConnect} />
        )}

        {activeTab === 'csv' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-grey-500">
                Upload a HubSpot CSV export to import contacts, organizations, and deals.
              </p>
              <Button size="sm" onClick={() => setShowImport(true)}>
                <Upload className="w-3.5 h-3.5" />
                Import CSV
              </Button>
            </div>
            <div className="space-y-2">
              {imports?.length === 0 ? (
                <div className="text-center py-8">
                  <FileSpreadsheet className="w-10 h-10 text-grey-300 mx-auto mb-2" />
                  <p className="text-grey-500 text-sm">No imports yet</p>
                  <p className="text-xs text-grey-400 mt-1">
                    Upload a CSV export from HubSpot to get started
                  </p>
                </div>
              ) : (
                imports?.map((importItem) => (
                  <ImportRow key={importItem.id} importItem={importItem} />
                ))
              )}
            </div>
          </div>
        )}
      </Card>

      {showImport && (
        <ImportWizardDialog open={showImport} onOpenChange={setShowImport} />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   HubSpot API Import Tab
   ───────────────────────────────────────────── */

type EntityTab = 'deals' | 'contacts' | 'companies';

function HubspotApiImportTab({ isConnected, onConnect }: { isConnected: boolean; onConnect: () => void }) {
  const [entityTab, setEntityTab] = useState<EntityTab>('deals');

  if (!isConnected) {
    return (
      <div className="text-center py-10">
        <ExternalLink className="w-10 h-10 text-grey-300 mx-auto mb-3" />
        <p className="text-sm text-grey-600 mb-1">Connect HubSpot to browse and import</p>
        <p className="text-xs text-grey-400 mb-4">
          You need to connect your HubSpot account to import deals, contacts, and companies via API.
        </p>
        <Button size="sm" onClick={onConnect}>
          <Link className="w-3.5 h-3.5" />
          Connect HubSpot
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Entity type sub-tabs */}
      <div className="flex items-center gap-1 mb-4">
        {([
          { key: 'deals' as const, label: 'Deals', icon: Briefcase },
          { key: 'contacts' as const, label: 'Contacts', icon: Users },
          { key: 'companies' as const, label: 'Companies', icon: Building2 },
        ]).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setEntityTab(key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              entityTab === key
                ? 'bg-primary/10 text-primary'
                : 'text-grey-500 hover:text-secondary hover:bg-grey-50'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {entityTab === 'deals' && <DealsPanel />}
      {entityTab === 'contacts' && <ContactsPanel />}
      {entityTab === 'companies' && <CompaniesPanel />}
    </div>
  );
}

/* ─── Deals Panel (extracted from original HubspotApiImportTab) ─── */

function DealsPanel() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('open');
  const [pipeline, setPipeline] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedDeals, setSelectedDeals] = useState<Set<string>>(new Set());
  const [targetPipelineId, setTargetPipelineId] = useState<string>('');

  const { data: pipelines } = useHubspotApiPipelines(true);
  const { data: solidPipelines } = usePipelines();
  const { data: dealsData, isLoading } = useHubspotApiDeals({
    search: search || undefined,
    limit: 20,
    status: status || undefined,
    pipeline: pipeline || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });
  const importMutation = useImportHubspotApiDeals();

  const stageLabels: Record<string, string> = {};
  if (pipelines) {
    for (const p of pipelines) {
      for (const s of p.stages) {
        stageLabels[s.id] = s.label;
      }
    }
  }

  const toggleDeal = (id: string) => {
    setSelectedDeals((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleImport = async () => {
    await importMutation.mutateAsync({
      dealIds: Array.from(selectedDeals),
      stageMapping: {},
      pipelineId: targetPipelineId || undefined,
    });
    setSelectedDeals(new Set());
  };

  return (
    <div>
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-400" />
        <input
          type="text"
          placeholder="Search HubSpot deals..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="text-sm border border-grey-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        >
          <option value="">All Statuses</option>
          <option value="open">Open Deals</option>
          <option value="closed">Closed Deals</option>
        </select>

        <select
          value={pipeline}
          onChange={(e) => setPipeline(e.target.value)}
          className="text-sm border border-grey-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        >
          <option value="">All Pipelines</option>
          {pipelines?.map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>

        <div className="flex items-center gap-1.5 text-sm text-grey-500">
          <span>From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-grey-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
          <span>to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-grey-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>

        {(status || pipeline || dateFrom || dateTo) && (
          <button
            onClick={() => { setStatus('open'); setPipeline(''); setDateFrom(''); setDateTo(''); }}
            className="text-xs text-grey-500 hover:text-secondary underline"
          >
            Reset filters
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      ) : !dealsData?.deals?.length ? (
        <div className="text-center py-8">
          <p className="text-sm text-grey-500">No deals found</p>
        </div>
      ) : (
        <>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {dealsData.deals.map((deal) => (
              <label
                key={deal.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedDeals.has(deal.id)
                    ? 'border-primary bg-primary/5'
                    : 'border-grey-200 hover:border-grey-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedDeals.has(deal.id)}
                  onChange={() => toggleDeal(deal.id)}
                  className="rounded border-grey-300 text-primary focus:ring-primary"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-secondary truncate">
                    {deal.properties.dealname}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-grey-500">
                    {deal.properties.amount && (
                      <span>${Number(deal.properties.amount).toLocaleString()}</span>
                    )}
                    {deal.properties.dealstage && (
                      <span>Stage: {stageLabels[deal.properties.dealstage] || deal.properties.dealstage}</span>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>

          {selectedDeals.size > 0 && (
            <div className="mt-4 flex items-center justify-between p-3 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-sm text-primary font-medium">
                {selectedDeals.size} deal{selectedDeals.size !== 1 ? 's' : ''} selected
              </p>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <label htmlFor="api-target-pipeline" className="text-xs text-grey-600 whitespace-nowrap">Import to:</label>
                  <select
                    id="api-target-pipeline"
                    value={targetPipelineId}
                    onChange={(e) => setTargetPipelineId(e.target.value)}
                    className="text-sm border border-grey-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  >
                    {solidPipelines?.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}{p.isDefault ? ' (Default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  size="sm"
                  onClick={handleImport}
                  loading={importMutation.isPending}
                >
                  Import Selected
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Contacts Panel ─── */

function ContactsPanel() {
  const [search, setSearch] = useState('');
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [importLinkedCompanies, setImportLinkedCompanies] = useState(true);
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number } | null>(null);

  const { data: contactsData, isLoading } = useHubspotApiContacts({
    search: search || undefined,
    limit: 20,
  });
  const importMutation = useImportHubspotApiContacts();

  const toggleContact = (id: string) => {
    setSelectedContacts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleImport = async () => {
    const result = await importMutation.mutateAsync({
      contactIds: Array.from(selectedContacts),
      importLinkedCompanies,
    });
    setImportResult({ imported: result.imported, skipped: result.skipped });
    setSelectedContacts(new Set());
  };

  return (
    <div>
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-400" />
        <input
          type="text"
          placeholder="Search HubSpot contacts..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setImportResult(null); }}
          className="w-full pl-10 pr-4 py-2 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
      </div>

      {importResult && (
        <div className="flex items-center gap-2 mb-3 p-3 rounded-lg bg-success/5 border border-success/20 text-sm">
          <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
          <span className="text-success">
            Imported {importResult.imported} contact{importResult.imported !== 1 ? 's' : ''}
            {importResult.skipped > 0 && <>, {importResult.skipped} skipped (already exist)</>}
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      ) : !contactsData?.contacts?.length ? (
        <div className="text-center py-8">
          <Users className="w-8 h-8 text-grey-300 mx-auto mb-2" />
          <p className="text-sm text-grey-500">No contacts found</p>
        </div>
      ) : (
        <>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {contactsData.contacts.map((contact) => {
              const name = [contact.properties.firstname, contact.properties.lastname].filter(Boolean).join(' ') || 'Unnamed';
              return (
                <label
                  key={contact.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedContacts.has(contact.id)
                      ? 'border-primary bg-primary/5'
                      : 'border-grey-200 hover:border-grey-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedContacts.has(contact.id)}
                    onChange={() => toggleContact(contact.id)}
                    className="rounded border-grey-300 text-primary focus:ring-primary"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-secondary truncate">{name}</p>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-grey-500">
                      {contact.properties.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {contact.properties.email}
                        </span>
                      )}
                      {contact.properties.company && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {contact.properties.company}
                        </span>
                      )}
                      {contact.properties.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {contact.properties.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          {selectedContacts.size > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
              <div className="flex items-center justify-between">
                <p className="text-sm text-primary font-medium">
                  {selectedContacts.size} contact{selectedContacts.size !== 1 ? 's' : ''} selected
                </p>
                <Button
                  size="sm"
                  onClick={handleImport}
                  loading={importMutation.isPending}
                >
                  Import Selected
                </Button>
              </div>
              <label className="flex items-center gap-2 mt-2 text-xs text-grey-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={importLinkedCompanies}
                  onChange={(e) => setImportLinkedCompanies(e.target.checked)}
                  className="rounded border-grey-300 text-primary focus:ring-primary"
                />
                Import linked companies as organizations
              </label>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Companies Panel ─── */

function CompaniesPanel() {
  const [search, setSearch] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(new Set());
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number } | null>(null);

  const { data: companiesData, isLoading } = useHubspotApiCompanies({
    search: search || undefined,
    limit: 20,
  });
  const importMutation = useImportHubspotApiCompanies();

  const toggleCompany = (id: string) => {
    setSelectedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleImport = async () => {
    const result = await importMutation.mutateAsync({
      companyIds: Array.from(selectedCompanies),
    });
    setImportResult({ imported: result.imported, skipped: result.skipped });
    setSelectedCompanies(new Set());
  };

  return (
    <div>
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-400" />
        <input
          type="text"
          placeholder="Search HubSpot companies..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setImportResult(null); }}
          className="w-full pl-10 pr-4 py-2 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
      </div>

      {importResult && (
        <div className="flex items-center gap-2 mb-3 p-3 rounded-lg bg-success/5 border border-success/20 text-sm">
          <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
          <span className="text-success">
            Imported {importResult.imported} compan{importResult.imported !== 1 ? 'ies' : 'y'}
            {importResult.skipped > 0 && <>, {importResult.skipped} skipped (already exist)</>}
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      ) : !companiesData?.companies?.length ? (
        <div className="text-center py-8">
          <Building2 className="w-8 h-8 text-grey-300 mx-auto mb-2" />
          <p className="text-sm text-grey-500">No companies found</p>
        </div>
      ) : (
        <>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {companiesData.companies.map((company) => (
              <label
                key={company.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedCompanies.has(company.id)
                    ? 'border-primary bg-primary/5'
                    : 'border-grey-200 hover:border-grey-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedCompanies.has(company.id)}
                  onChange={() => toggleCompany(company.id)}
                  className="rounded border-grey-300 text-primary focus:ring-primary"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-secondary truncate">
                    {company.properties.name || 'Unnamed'}
                  </p>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-grey-500">
                    {company.properties.domain && (
                      <span>{company.properties.domain}</span>
                    )}
                    {company.properties.industry && (
                      <span>{company.properties.industry}</span>
                    )}
                    {company.properties.numberofemployees && (
                      <span>{company.properties.numberofemployees} employees</span>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>

          {selectedCompanies.size > 0 && (
            <div className="mt-4 flex items-center justify-between p-3 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-sm text-primary font-medium">
                {selectedCompanies.size} compan{selectedCompanies.size !== 1 ? 'ies' : 'y'} selected
              </p>
              <Button
                size="sm"
                onClick={handleImport}
                loading={importMutation.isPending}
              >
                Import Selected
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ImportRow({ importItem }: { importItem: HubspotImport }) {
  const [showErrors, setShowErrors] = useState(false);
  const badge = STATUS_BADGE[importItem.status];

  return (
    <div className="rounded-lg bg-grey-50 p-3">
      <div className="flex items-center gap-3">
        <FileSpreadsheet className="w-5 h-5 text-grey-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-secondary truncate">
              {importItem.fileName}
            </span>
            <Badge variant={badge.variant} size="sm">
              {badge.label}
            </Badge>
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-grey-500">
            <span>{importItem.totalRecords} records</span>
            {importItem.status === 'COMPLETED' && (
              <>
                <span>{importItem.importedDeals} deals</span>
                <span>{importItem.importedContacts} contacts</span>
                <span>{importItem.importedOrgs} orgs</span>
                {importItem.skippedRecords > 0 && (
                  <span className="text-warning">{importItem.skippedRecords} skipped</span>
                )}
              </>
            )}
            {importItem.user && <span>by {importItem.user.name}</span>}
            <span>{formatRelativeTime(importItem.createdAt)}</span>
          </div>
        </div>

        {importItem.errors && importItem.errors.length > 0 && (
          <button
            onClick={() => setShowErrors(!showErrors)}
            className="flex items-center gap-1 text-xs text-error hover:text-error/80"
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            {importItem.errors.length} errors
          </button>
        )}
      </div>

      {showErrors && importItem.errors && (
        <div className="mt-3 border border-error/20 rounded-lg bg-error/5 p-3 max-h-40 overflow-y-auto">
          <div className="space-y-1">
            {importItem.errors.map((error, i) => (
              <p key={i} className="text-xs text-error">
                <span className="font-medium">Row {error.row}:</span> {error.message}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Import Wizard Dialog
   ───────────────────────────────────────────── */

type WizardStep = 'upload' | 'mapping' | 'processing' | 'results';

function ImportWizardDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [step, setStep] = useState<WizardStep>('upload');
  const [importId, setImportId] = useState<string | null>(null);

  const handleUploadComplete = (id: string) => {
    setImportId(id);
    setStep('mapping');
  };

  const handleMappingComplete = () => {
    setStep('processing');
  };

  const handleProcessingComplete = () => {
    setStep('results');
  };

  const handleClose = () => {
    onOpenChange(false);
    setStep('upload');
    setImportId(null);
  };

  const stepLabels: Record<WizardStep, string> = {
    upload: 'Upload CSV',
    mapping: 'Map Fields',
    processing: 'Processing',
    results: 'Results',
  };

  const stepOrder: WizardStep[] = ['upload', 'mapping', 'processing', 'results'];
  const currentStepIndex = stepOrder.indexOf(step);

  return (
    <Modal open={open} onOpenChange={handleClose} title="Import from HubSpot" size="lg">
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {stepOrder.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                i < currentStepIndex
                  ? 'bg-success/10 text-success'
                  : i === currentStepIndex
                  ? 'bg-primary/10 text-primary'
                  : 'bg-grey-100 text-grey-400'
              }`}
            >
              {i < currentStepIndex ? (
                <CheckCircle className="w-3.5 h-3.5" />
              ) : (
                <span className="w-3.5 h-3.5 flex items-center justify-center text-[10px]">
                  {i + 1}
                </span>
              )}
              {stepLabels[s]}
            </div>
            {i < stepOrder.length - 1 && (
              <ArrowRight className="w-3.5 h-3.5 text-grey-300" />
            )}
          </div>
        ))}
      </div>

      {step === 'upload' && (
        <UploadStep onComplete={handleUploadComplete} onCancel={handleClose} />
      )}
      {step === 'mapping' && importId && (
        <MappingStep
          importId={importId}
          onComplete={handleMappingComplete}
          onBack={() => setStep('upload')}
        />
      )}
      {step === 'processing' && importId && (
        <ProcessingStep importId={importId} onComplete={handleProcessingComplete} />
      )}
      {step === 'results' && importId && (
        <ResultsStep importId={importId} onClose={handleClose} />
      )}
    </Modal>
  );
}

/* ─── Upload Step ─── */

function UploadStep({
  onComplete,
  onCancel,
}: {
  onComplete: (importId: string) => void;
  onCancel: () => void;
}) {
  const [dragActive, setDragActive] = useState(false);
  const uploadCsv = useUploadHubspotCsv();

  const handleFile = useCallback(
    async (file: File) => {
      const result = await uploadCsv.mutateAsync(file);
      onComplete(result.id);
    },
    [uploadCsv, onComplete]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files[0];
      if (file && file.name.endsWith('.csv')) {
        handleFile(file);
      }
    },
    [handleFile]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  return (
    <div>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors ${
          dragActive
            ? 'border-primary bg-primary/5'
            : 'border-grey-200 hover:border-grey-300'
        }`}
      >
        {uploadCsv.isPending ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
            <p className="text-sm text-grey-600">Uploading and parsing CSV...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <UploadCloud className="w-10 h-10 text-grey-300" />
            <div>
              <p className="text-sm text-grey-600">
                Drag and drop your HubSpot CSV export here
              </p>
              <p className="text-xs text-grey-400 mt-1">or click to browse</p>
            </div>
            <label>
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleInputChange}
              />
              <span className="btn-outline btn-sm cursor-pointer inline-flex items-center gap-1.5">
                <Upload className="w-3.5 h-3.5" />
                Choose File
              </span>
            </label>
          </div>
        )}
      </div>

      {uploadCsv.isError && (
        <div className="mt-3 flex items-center gap-2 text-sm text-error">
          <XCircle className="w-4 h-4 flex-shrink-0" />
          <span>Failed to upload CSV. Please ensure the file is a valid CSV export from HubSpot.</span>
        </div>
      )}

      <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-grey-200">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/* ─── Mapping Step ─── */

function MappingStep({
  importId,
  onComplete,
  onBack,
}: {
  importId: string;
  onComplete: () => void;
  onBack: () => void;
}) {
  const { data: preview, isLoading } = useHubspotImportPreview(importId);
  const { data: solidPipelines } = usePipelines();
  const submitMapping = useSubmitHubspotMapping();
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [initialized, setInitialized] = useState(false);
  const [targetPipelineId, setTargetPipelineId] = useState<string>('');

  // Initialize mappings from suggestions once preview loads
  if (preview && !initialized) {
    const initial: Record<string, string> = {};
    for (const header of preview.headers) {
      initial[header] = preview.suggestedMappings[header] || 'skip';
    }
    setMappings(initial);
    setInitialized(true);
  }

  if (isLoading || !preview) {
    return (
      <div className="flex flex-col items-center gap-3 py-10">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <p className="text-sm text-grey-500">Loading preview...</p>
      </div>
    );
  }

  const handleMappingChange = (header: string, value: string) => {
    setMappings((prev) => ({ ...prev, [header]: value }));
  };

  const handleSubmit = async () => {
    await submitMapping.mutateAsync({
      importId,
      mappings,
      pipelineId: targetPipelineId || undefined,
    });
    onComplete();
  };

  const mappedCount = Object.values(mappings).filter((v) => v !== 'skip').length;

  return (
    <div>
      <div className="mb-4">
        <p className="text-sm text-grey-600">
          <span className="font-medium">{preview.fileName}</span> -
          {' '}{preview.totalRecords} records found, {preview.headers.length} columns
        </p>
        <p className="text-xs text-grey-400 mt-0.5">
          {mappedCount} of {preview.headers.length} columns mapped
        </p>
      </div>

      {/* Preview table */}
      <div className="border border-grey-200 rounded-lg overflow-x-auto mb-4 max-h-48">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-grey-100">
              {preview.headers.map((header) => (
                <th key={header} className="text-left px-3 py-2 font-medium text-grey-600 whitespace-nowrap">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-grey-100">
            {preview.previewRows.slice(0, 5).map((row, i) => (
              <tr key={i}>
                {preview.headers.map((header) => (
                  <td key={header} className="px-3 py-1.5 text-grey-500 whitespace-nowrap max-w-[200px] truncate">
                    {row[header] || '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Target pipeline selector */}
      <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-grey-50 border border-grey-200">
        <label htmlFor="csv-target-pipeline" className="text-sm font-medium text-secondary whitespace-nowrap">Target Pipeline:</label>
        <select
          id="csv-target-pipeline"
          value={targetPipelineId}
          onChange={(e) => setTargetPipelineId(e.target.value)}
          className="flex-1 text-sm border border-grey-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        >
          {solidPipelines?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}{p.isDefault ? ' (Default)' : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Field mapping */}
      <div className="space-y-3 max-h-64 overflow-y-auto">
        <p className="text-xs font-medium text-grey-500 uppercase tracking-wider">Column Mapping</p>
        {preview.headers.map((header) => {
          const sampleValues = preview.previewRows
            .slice(0, 3)
            .map((row) => row[header])
            .filter(Boolean);

          return (
            <div key={header} className="flex items-center gap-3 rounded-lg bg-grey-50 p-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-secondary">{header}</p>
                {sampleValues.length > 0 && (
                  <p className="text-xs text-grey-400 truncate mt-0.5">
                    e.g. {sampleValues.join(', ')}
                  </p>
                )}
              </div>
              <ArrowRight className="w-4 h-4 text-grey-300 flex-shrink-0" />
              <div className="w-56 flex-shrink-0">
                <Select
                  aria-label={`Map column ${header}`}
                  options={TARGET_FIELDS}
                  value={mappings[header] || 'skip'}
                  onChange={(e) => handleMappingChange(header, e.target.value)}
                  selectSize="sm"
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-grey-200">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          loading={submitMapping.isPending}
          disabled={mappedCount === 0}
        >
          Start Import
          <ArrowRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

/* ─── Processing Step ─── */

function ProcessingStep({
  importId,
  onComplete,
}: {
  importId: string;
  onComplete: () => void;
}) {
  const { data: importData } = useHubspotImport(importId, {
    refetchInterval: 2000,
  });

  // Auto-advance when processing is done
  if (importData && (importData.status === 'COMPLETED' || importData.status === 'FAILED')) {
    // Use a timeout to let the final state render briefly before advancing
    setTimeout(onComplete, 500);
  }

  const progress = importData
    ? Math.round(
        ((importData.importedDeals + importData.importedContacts + importData.importedOrgs) /
          Math.max(importData.totalRecords, 1)) *
          100
      )
    : 0;

  return (
    <div className="flex flex-col items-center gap-4 py-10">
      <Loader2 className="w-12 h-12 text-primary animate-spin" />
      <div className="text-center">
        <p className="text-sm font-medium text-secondary">Importing records...</p>
        <p className="text-xs text-grey-500 mt-1">
          {importData
            ? `${importData.importedDeals + importData.importedContacts + importData.importedOrgs} of ${importData.totalRecords} records processed`
            : 'Starting import...'}
        </p>
      </div>
      <div className="w-64 bg-grey-200 rounded-full h-2">
        <div
          className="bg-primary h-2 rounded-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="text-xs text-grey-400">
        This may take a few minutes. Do not close this dialog.
      </p>
    </div>
  );
}

/* ─── Results Step ─── */

function ResultsStep({
  importId,
  onClose,
}: {
  importId: string;
  onClose: () => void;
}) {
  const { data: importData, isLoading } = useHubspotImport(importId);

  if (isLoading || !importData) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const isSuccess = importData.status === 'COMPLETED';

  return (
    <div>
      <div className="flex flex-col items-center gap-3 py-6">
        {isSuccess ? (
          <CheckCircle className="w-12 h-12 text-success" />
        ) : (
          <XCircle className="w-12 h-12 text-error" />
        )}
        <p className="text-lg font-medium text-secondary">
          {isSuccess ? 'Import Complete' : 'Import Failed'}
        </p>
        <p className="text-sm text-grey-500">{importData.fileName}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <StatCard label="Deals Imported" value={importData.importedDeals} variant="primary" />
        <StatCard label="Contacts Imported" value={importData.importedContacts} variant="primary" />
        <StatCard label="Organizations Imported" value={importData.importedOrgs} variant="primary" />
        <StatCard label="Records Skipped" value={importData.skippedRecords} variant={importData.skippedRecords > 0 ? 'warning' : 'default'} />
      </div>

      {importData.errors && importData.errors.length > 0 && (
        <div className="border border-error/20 rounded-lg bg-error/5 p-3 mb-4 max-h-40 overflow-y-auto">
          <p className="text-xs font-medium text-error mb-2">
            {importData.errors.length} error{importData.errors.length !== 1 ? 's' : ''} occurred:
          </p>
          <div className="space-y-1">
            {importData.errors.map((error, i) => (
              <p key={i} className="text-xs text-error/80">
                <span className="font-medium">Row {error.row}:</span> {error.message}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-grey-200">
        <Button onClick={onClose}>Done</Button>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  variant = 'default',
}: {
  label: string;
  value: number;
  variant?: 'default' | 'primary' | 'warning';
}) {
  const variantStyles = {
    default: 'bg-grey-50 text-secondary',
    primary: 'bg-primary/5 text-primary',
    warning: 'bg-warning/5 text-warning',
  };

  return (
    <div className={`rounded-lg p-3 ${variantStyles[variant]}`}>
      <p className="text-xs text-grey-500">{label}</p>
      <p className="text-xl font-semibold mt-0.5">{value}</p>
    </div>
  );
}
